/**
 * Tooltips and menus. Thin styling over Radix, which supplies the behaviour: keyboard
 * navigation, typeahead, focus return, collision-aware placement.
 */

import {
  ContextMenu as ContextPrimitive,
  DropdownMenu as MenuPrimitive,
  Tooltip as TooltipPrimitive,
} from 'radix-ui';
import { type ReactNode } from 'react';

import { cn } from './cn.ts';
import { Kbd } from './kbd.tsx';

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  content,
  children,
  side = 'bottom',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <TooltipPrimitive.Root delayDuration={400}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 max-w-xs rounded-md border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-lg"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export interface MenuItemSpec {
  id: string;
  label: string;
  icon?: ReactNode;
  shortcut?: string | undefined;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export type MenuEntry = MenuItemSpec | 'separator';

const MENU =
  'z-50 min-w-48 rounded-lg border border-border bg-surface-raised p-1 text-sm shadow-xl';
const ITEM =
  'flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-fg outline-none select-none ' +
  'data-[disabled]:opacity-40 data-[highlighted]:bg-accent-soft';

function ItemBody({ item }: { item: MenuItemSpec }) {
  return (
    <>
      <span className="flex size-4 items-center justify-center text-fg-muted" aria-hidden>
        {item.icon}
      </span>
      <span className={cn('flex-1', item.danger && 'text-danger')}>{item.label}</span>
      {item.shortcut ? <Kbd>{item.shortcut}</Kbd> : null}
    </>
  );
}

export function DropdownMenu({
  trigger,
  items,
  align = 'start',
}: {
  trigger: ReactNode;
  items: readonly MenuEntry[];
  align?: 'start' | 'end';
}) {
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger asChild>{trigger}</MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content align={align} sideOffset={4} className={MENU}>
          {items.map((item, i) =>
            item === 'separator' ? (
              <MenuPrimitive.Separator key={`sep-${i}`} className="my-1 h-px bg-border" />
            ) : (
              <MenuPrimitive.Item
                key={item.id}
                disabled={item.disabled ?? false}
                onSelect={item.onSelect}
                className={ITEM}
              >
                <ItemBody item={item} />
              </MenuPrimitive.Item>
            ),
          )}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}

export function ContextMenu({
  children,
  items,
}: {
  children: ReactNode;
  items: readonly MenuEntry[];
}) {
  return (
    <ContextPrimitive.Root>
      <ContextPrimitive.Trigger asChild>{children}</ContextPrimitive.Trigger>
      <ContextPrimitive.Portal>
        <ContextPrimitive.Content className={MENU}>
          {items.map((item, i) =>
            item === 'separator' ? (
              <ContextPrimitive.Separator key={`sep-${i}`} className="my-1 h-px bg-border" />
            ) : (
              <ContextPrimitive.Item
                key={item.id}
                disabled={item.disabled ?? false}
                onSelect={item.onSelect}
                className={ITEM}
              >
                <ItemBody item={item} />
              </ContextPrimitive.Item>
            ),
          )}
        </ContextPrimitive.Content>
      </ContextPrimitive.Portal>
    </ContextPrimitive.Root>
  );
}
